import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Save, Play, RefreshCw, Clock, CheckCircle2,
  XCircle, AlertTriangle, Database, KeyRound, History, Timer,
  Upload, FileSpreadsheet,
} from "lucide-react";

const PRESET_TIMES = {
  1: ["12:00"],
  2: ["00:00", "12:00"],
  4: ["00:00", "06:00", "12:00", "18:00"],
  6: ["00:00", "04:00", "08:00", "12:00", "16:00", "20:00"],
  12: ["00:00", "02:00", "04:00", "06:00", "08:00", "10:00", "12:00", "14:00", "16:00", "18:00", "20:00", "22:00"],
  24: Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`),
};

export default function AutoCrawlTab() {
  const [modules, setModules] = useState([]);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Credentials form
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [retailer, setRetailer] = useState("karinopr");
  const [savingCred, setSavingCred] = useState(false);

  // Schedule form
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [runsPerDay, setRunsPerDay] = useState(4);
  const [times, setTimes] = useState([]);
  const [enabledModules, setEnabledModules] = useState([]);
  const [daysBack, setDaysBack] = useState(7);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const [running, setRunning] = useState(false);
  const [alltimeConfirm, setAlltimeConfirm] = useState(false);

  // Excel upload for broken modules (cashbook, pricebooks)
  const [uploadingExcel, setUploadingExcel] = useState("");

  const handleExcelUpload = async (moduleKey, file) => {
    if (!file) return;
    setUploadingExcel(moduleKey);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("replace", "false");
      const { data } = await api.post(`/kiotviet/crawl/excel-import/${moduleKey}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.message} (thêm ${data.inserted}, cập nhật ${data.updated})`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi import Excel");
    } finally {
      setUploadingExcel("");
    }
  };

  const loadAll = useCallback(async () => {
    try {
      const [modsR, cfgR, statR, logsR] = await Promise.all([
        api.get("/kiotviet/crawl/modules"),
        api.get("/kiotviet/crawl/config"),
        api.get("/kiotviet/crawl/status"),
        api.get("/kiotviet/crawl/logs?days=30"),
      ]);
      setModules(modsR.data.modules || []);
      setConfig(cfgR.data);
      setStatus(statR.data);
      setLogs(logsR.data.logs || []);

      setUsername(cfgR.data.credentials.username || "");
      setRetailer(cfgR.data.credentials.retailer || "karinopr");
      setScheduleEnabled(cfgR.data.schedule.enabled);
      setRunsPerDay(cfgR.data.schedule.runs_per_day);
      setTimes(cfgR.data.schedule.times || []);
      setEnabledModules(cfgR.data.schedule.enabled_modules || []);
      setDaysBack(cfgR.data.schedule.days_back || 7);
    } catch (err) {
      toast.error("Không tải được cấu hình");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh status when running
  useEffect(() => {
    if (!running && !status?.active) return;
    const iv = setInterval(() => {
      api.get("/kiotviet/crawl/status").then(r => {
        setStatus(r.data);
        if (!r.data.active && running) {
          setRunning(false);
          api.get("/kiotviet/crawl/logs?days=30").then(lr => setLogs(lr.data.logs || []));
        }
      });
    }, 2000);
    return () => clearInterval(iv);
  }, [running, status?.active]);

  const handleSaveCreds = async () => {
    if (!username || !password) {
      toast.error("Nhập đầy đủ username + password");
      return;
    }
    setSavingCred(true);
    try {
      const { data } = await api.post("/kiotviet/crawl/credentials", { username, password, retailer });
      toast.success(data.message);
      setPassword("");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login thất bại");
    } finally {
      setSavingCred(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const body = {
        enabled: scheduleEnabled,
        runs_per_day: runsPerDay,
        times,
        enabled_modules: enabledModules,
        days_back: daysBack,
      };
      const { data } = await api.post("/kiotviet/crawl/schedule", body);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi lưu");
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const body = {
        modules: enabledModules.length > 0 ? enabledModules : null,
        days_back: daysBack,
      };
      const { data } = await api.post("/kiotviet/crawl/run-now", body);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi trigger");
      setRunning(false);
    }
  };

  const handleAlltime = async () => {
    setRunning(true);
    setAlltimeConfirm(false);
    try {
      const { data } = await api.post("/kiotviet/crawl/alltime", {});
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi trigger");
      setRunning(false);
    }
  };

  const changeRunsPerDay = (n) => {
    setRunsPerDay(n);
    setTimes(PRESET_TIMES[n] || []);
  };

  const toggleModule = (key) => {
    setEnabledModules(prev =>
      prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]
    );
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#4680ff] animate-spin" /></div>;

  const active = status?.active;
  const latest = status?.latest;
  const credsSaved = config?.credentials?.password_set;
  const sessionValid = config?.session?.valid;

  return (
    <div className="space-y-4" data-testid="auto-crawl-tab">
      {/* ======== CREDENTIALS ======== */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-bold text-[#F8FAFC]">1. Credentials đăng nhập KiotViet</h3>
          {credsSaved && sessionValid && (
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-[10px]">
              <CheckCircle2 className="w-3 h-3 mr-1" />Session OK
            </Badge>
          )}
          {credsSaved && !sessionValid && (
            <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-[10px]">
              <AlertTriangle className="w-3 h-3 mr-1" />Session hết hạn
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <Input
            placeholder="SDT đăng nhập (0935...)"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="bg-[#0f172a] border-[#334155] text-xs h-9"
            data-testid="crawl-username"
          />
          <Input
            type="password"
            placeholder={credsSaved ? "●●●●● (đã lưu)" : "Mật khẩu"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-[#0f172a] border-[#334155] text-xs h-9"
            data-testid="crawl-password"
          />
          <Input
            placeholder="Retailer (karinopr)"
            value={retailer}
            onChange={(e) => setRetailer(e.target.value)}
            className="bg-[#0f172a] border-[#334155] text-xs h-9"
            data-testid="crawl-retailer"
          />
          <Button
            onClick={handleSaveCreds}
            disabled={savingCred}
            className="bg-[#0162e8] hover:bg-[#0150c0] text-white text-xs h-9"
            data-testid="save-creds-btn"
          >
            {savingCred ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Lưu + Test login
          </Button>
        </div>
        <p className="text-[10px] text-slate-500 mt-2">
          Crawler sẽ login vào KiotViet Web UI và gọi JSON API nội bộ. Session cache 12h, tự động refresh.
        </p>
      </div>

      {/* ======== SCHEDULE ======== */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-[#4680ff]" />
          <h3 className="text-sm font-bold text-[#F8FAFC]">2. Cấu hình lịch chạy tự động</h3>
          <label className="ml-auto flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={(e) => setScheduleEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-slate-500 text-lime-500"
              data-testid="toggle-schedule-enabled"
            />
            <span className="text-xs text-slate-300">Bật crawl tự động</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wide">Số lần/ngày</label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 4, 6, 12, 24].map(n => (
                <button
                  key={n}
                  onClick={() => changeRunsPerDay(n)}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    runsPerDay === n
                      ? "bg-[#4680ff] text-white"
                      : "bg-[#0f172a] text-slate-400 border border-[#334155] hover:border-[#4680ff]"
                  }`}
                  data-testid={`runs-per-day-${n}`}
                >
                  {n}x
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wide">Dữ liệu mỗi lần</label>
            <div className="flex gap-1 mt-1">
              {[1, 3, 7, 14, 30].map(d => (
                <button
                  key={d}
                  onClick={() => setDaysBack(d)}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    daysBack === d
                      ? "bg-teal-600 text-white"
                      : "bg-[#0f172a] text-slate-400 border border-[#334155] hover:border-teal-500"
                  }`}
                  data-testid={`days-back-${d}`}
                >
                  {d} ngày
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label className="text-[10px] text-slate-400 uppercase tracking-wide">Giờ chạy (HH:MM, VN)</label>
          <div className="flex gap-1 flex-wrap mt-1">
            {times.map(t => (
              <span key={t} className="bg-[#0f172a] border border-[#334155] rounded px-2 py-1 text-xs text-slate-300 font-mono">
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="mb-3">
          <label className="text-[10px] text-slate-400 uppercase tracking-wide">Module crawl (bật/tắt từng tab)</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-1">
            {modules.map(m => (
              <label
                key={m.key}
                className="flex items-center gap-2 bg-[#0f172a] border border-[#334155] rounded px-2 py-1.5 cursor-pointer hover:border-[#4680ff]"
              >
                <input
                  type="checkbox"
                  checked={enabledModules.includes(m.key)}
                  onChange={() => toggleModule(m.key)}
                  className="w-3.5 h-3.5 rounded border-slate-500 text-lime-500"
                  data-testid={`toggle-module-${m.key}`}
                />
                <span className="text-xs text-slate-300">{m.label}</span>
                <code className="ml-auto text-[9px] text-slate-600">{m.collection}</code>
              </label>
            ))}
          </div>
        </div>

        <Button
          onClick={handleSaveSchedule}
          disabled={savingSchedule}
          className="bg-[#4680ff] hover:bg-[#3a70ef] text-white text-xs h-9 w-full"
          data-testid="save-schedule-btn"
        >
          {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Lưu cấu hình lịch
        </Button>
      </div>

      {/* ======== RUN ACTIONS ======== */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Play className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-bold text-[#F8FAFC]">3. Chạy crawl</h3>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={handleRunNow}
            disabled={running || !credsSaved}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9"
            data-testid="run-now-btn"
          >
            {running && active?.type !== "alltime" ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Đang chạy...</>
            ) : (
              <><Play className="w-3.5 h-3.5 mr-1" /> Chạy ngay ({daysBack} ngày)</>
            )}
          </Button>
          {!alltimeConfirm ? (
            <Button
              onClick={() => setAlltimeConfirm(true)}
              disabled={running || !credsSaved}
              variant="outline"
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs h-9"
              data-testid="alltime-btn"
            >
              <Database className="w-3.5 h-3.5 mr-1" /> Đồng bộ Alltime
            </Button>
          ) : (
            <div className="flex gap-1 items-center bg-red-500/10 border border-red-500/30 rounded px-2">
              <span className="text-[10px] text-red-300 px-1">Xoá hết dữ liệu cũ?</span>
              <Button
                onClick={handleAlltime}
                className="bg-red-600 hover:bg-red-700 text-white text-[10px] h-7 px-2"
                data-testid="alltime-confirm-btn"
              >
                Có, xoá
              </Button>
              <Button
                onClick={() => setAlltimeConfirm(false)}
                variant="outline"
                className="border-slate-500/30 text-slate-400 text-[10px] h-7 px-2"
              >
                Huỷ
              </Button>
            </div>
          )}
        </div>
        {!credsSaved && (
          <p className="text-[10px] text-amber-400 mt-2">⚠ Cần lưu credentials trước.</p>
        )}
      </div>

      {/* ======== EXCEL UPLOAD FALLBACK (cho module KiotViet API lỗi) ======== */}
      <div className="bg-[#1e293b] border border-amber-500/30 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileSpreadsheet className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-bold text-[#F8FAFC]">4. Upload Excel thủ công (fallback)</h3>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-[10px]">
            KiotViet API lỗi
          </Badge>
        </div>
        <p className="text-[10px] text-slate-500 mb-3">
          2 module dưới đây KiotViet API không trả dữ liệu (Sổ quỹ bị lỗi 500 server-side, Thiết lập giá cần ít nhất 1 bảng giá). 
          Anh có thể tải file Excel từ trang KiotViet Web và upload vào đây.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {[
            { key: "cashbook", label: "Sổ quỹ", hint: "Tải Excel từ KiotViet → Sổ quỹ → Xuất file" },
            { key: "pricebooks", label: "Thiết lập giá", hint: "Tải Excel từ KiotViet → Thiết lập giá → Xuất file" },
          ].map(m => (
            <label
              key={m.key}
              className={`bg-[#0f172a] border border-[#334155] rounded-lg p-3 cursor-pointer hover:border-amber-500 transition-colors ${uploadingExcel === m.key ? "opacity-50 pointer-events-none" : ""}`}
              data-testid={`excel-upload-${m.key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                {uploadingExcel === m.key ? (
                  <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 text-amber-400" />
                )}
                <span className="text-xs font-medium text-slate-200">{m.label}</span>
              </div>
              <p className="text-[10px] text-slate-500">{m.hint}</p>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => handleExcelUpload(m.key, e.target.files?.[0])}
                className="hidden"
                disabled={uploadingExcel === m.key}
              />
            </label>
          ))}
        </div>
      </div>

      {/* ======== ACTIVE PROGRESS ======== */}
      {active && (
        <div className="bg-[#1e293b] border border-[#4680ff] rounded-xl p-4" data-testid="active-progress">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="w-4 h-4 text-[#4680ff] animate-spin" />
            <h3 className="text-sm font-bold text-[#F8FAFC]">Đang chạy: {active.type}</h3>
            <Badge className="bg-[#4680ff]/20 text-[#4680ff] text-[10px]">{active.modules?.length || 0} / {enabledModules.length} module</Badge>
          </div>
          <div className="space-y-1">
            {(active.modules || []).map(m => (
              <div key={m.module} className="flex items-center gap-2 text-xs">
                {m.status === "success" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                {m.status === "error" && <XCircle className="w-3.5 h-3.5 text-red-400" />}
                {m.status === "running" && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#4680ff]" />}
                <span className="text-slate-300">{m.label}</span>
                {m.status === "success" && (
                  <span className="text-slate-500 text-[10px] ml-auto">
                    +{m.inserted} mới, ↻{m.updated} cập nhật
                  </span>
                )}
                {m.status === "error" && (
                  <span className="text-red-400 text-[10px] ml-auto truncate max-w-xs">{m.error}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ======== LATEST RESULT ======== */}
      {!active && latest && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="latest-result">
          <div className="flex items-center gap-2 mb-2">
            <Timer className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-bold text-[#F8FAFC]">Lần chạy cuối</h3>
            <Badge className={`text-[10px] ${
              latest.status === "success" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
              : latest.status === "partial" ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
              : "bg-red-500/10 text-red-400 border-red-500/30"
            }`}>
              {latest.status}
            </Badge>
            <span className="text-slate-500 text-[10px] ml-auto">
              {latest.started_at?.slice(5, 16).replace("T", " ")} • {latest.duration_s?.toFixed(1)}s
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-2 text-xs">
            <div className="bg-[#0f172a] rounded px-2 py-1">
              <span className="text-slate-500 text-[10px]">Thêm mới: </span>
              <span className="text-emerald-400 font-bold">{latest.total_inserted}</span>
            </div>
            <div className="bg-[#0f172a] rounded px-2 py-1">
              <span className="text-slate-500 text-[10px]">Cập nhật: </span>
              <span className="text-[#4680ff] font-bold">{latest.total_updated}</span>
            </div>
            <div className="bg-[#0f172a] rounded px-2 py-1">
              <span className="text-slate-500 text-[10px]">Lỗi: </span>
              <span className="text-red-400 font-bold">{latest.total_errors}</span>
            </div>
          </div>
          <div className="space-y-1">
            {(latest.modules || []).map(m => (
              <div key={m.module} className="flex items-center gap-2 text-xs">
                {m.status === "success" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                {m.status === "error" && <XCircle className="w-3.5 h-3.5 text-red-400" />}
                <span className="text-slate-300">{m.label}</span>
                {m.status === "success" && (
                  <span className="text-slate-500 text-[10px] ml-auto">
                    +{m.inserted} / ↻{m.updated} ({m.total_fetched} fetched)
                  </span>
                )}
                {m.status === "error" && (
                  <span className="text-red-400 text-[10px] ml-auto truncate max-w-xs">{m.error}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ======== LOGS (30 days) ======== */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <History className="w-4 h-4 text-slate-400" />
          <h3 className="text-sm font-bold text-[#F8FAFC]">Lịch sử 30 ngày</h3>
          <span className="text-[10px] text-slate-500 ml-auto">{logs.length} lượt chạy</span>
          <button onClick={loadAll} className="text-slate-500 hover:text-slate-300"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
        <div className="overflow-auto max-h-80">
          <table className="w-full text-xs" data-testid="crawl-logs-table">
            <thead className="text-slate-500 text-[10px] uppercase tracking-wide">
              <tr>
                <th className="text-left py-1 pr-2">Lúc</th>
                <th className="text-left py-1 pr-2">Type</th>
                <th className="text-left py-1 pr-2">Status</th>
                <th className="text-right py-1 pr-2">Mới</th>
                <th className="text-right py-1 pr-2">Cập nhật</th>
                <th className="text-right py-1 pr-2">Lỗi</th>
                <th className="text-right py-1">Duration</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.run_id} className="border-t border-[#334155] hover:bg-[#0f172a]/60">
                  <td className="py-1 pr-2 text-slate-400 font-mono">{log.started_at?.slice(5, 16).replace("T", " ")}</td>
                  <td className="py-1 pr-2 text-slate-300">{log.type}</td>
                  <td className="py-1 pr-2">
                    <Badge className={`text-[10px] ${
                      log.status === "success" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                      : log.status === "partial" ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
                      : log.status === "running" ? "bg-[#4680ff]/10 text-[#4680ff] border-[#4680ff]/30"
                      : "bg-red-500/10 text-red-400 border-red-500/30"
                    }`}>
                      {log.status}
                    </Badge>
                  </td>
                  <td className="py-1 pr-2 text-right text-emerald-400">{log.total_inserted || 0}</td>
                  <td className="py-1 pr-2 text-right text-[#4680ff]">{log.total_updated || 0}</td>
                  <td className="py-1 pr-2 text-right text-red-400">{log.total_errors || 0}</td>
                  <td className="py-1 text-right text-slate-500">{log.duration_s?.toFixed(1)}s</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan="7" className="text-center text-slate-500 py-4">Chưa có log</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
